import Phaser from 'phaser';
import {
  getPatientDefinition,
  getRoomDefinition,
  getRoomVisualDefinition,
  Simulation
} from '../../sim/Simulation';
import type { BuildFailReason } from '../../sim/types';
import { FAIL_MESSAGES, HUD } from '../../ui/hud';

export class GameScene extends Phaser.Scene {
  private sim!: Simulation;
  private hud!: HUD;

  private accumulator: number = 0;

  private readonly cellWidth = 92;
  private readonly cellHeight = 72;
  private originX = 90;
  private originY = 160;

  private backgroundGraphics!: Phaser.GameObjects.Graphics;
  private buildingGraphics!: Phaser.GameObjects.Graphics;
  private entitiesGraphics!: Phaser.GameObjects.Graphics;
  private previewGraphics!: Phaser.GameObjects.Graphics;

  private roomLabels: Phaser.GameObjects.Text[] = [];

  private activeRoomTypeId: string | null = null;
  private previewFloor: number | null = null;
  private previewCol: number | null = null;

  private lastTappedCoord: { floor: number; col: number } | null = null;

  constructor() {
    super('GameScene');
  }

  public init(data: { sim: Simulation; hud: HUD }): void {
    this.sim = data.sim;
    this.hud = data.hud;
  }

  public create(): void {
    this.backgroundGraphics = this.add.graphics();
    this.buildingGraphics = this.add.graphics();
    this.entitiesGraphics = this.add.graphics();
    this.previewGraphics = this.add.graphics();

    const { cols, floors } = this.sim.getState().gridConfig;
    const totalWidth = cols * this.cellWidth;
    this.originX = Math.round((1280 - totalWidth) / 2);
    this.originY = 120;

    this.drawStaticEnvironment(cols, floors);
    this.redrawBuilding();

    this.hud.onSelectRoom((typeId: string | null) => {
      this.activeRoomTypeId = typeId;
      this.lastTappedCoord = null;
      if (!typeId) {
        this.previewFloor = null;
        this.previewCol = null;
        this.previewGraphics.clear();
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.handlePointerMove(pointer);
    });

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handlePointerDown(pointer);
    });
  }

  public override update(_time: number, rawDelta: number): void {
    const result = this.sim.advanceFrame(rawDelta, this.accumulator);
    this.accumulator = result.accumulator;

    this.renderEntities();
    this.hud.update();
  }

  public syncFromState(): void {
    this.accumulator = 0;
    this.activeRoomTypeId = null;
    this.previewFloor = null;
    this.previewCol = null;
    this.lastTappedCoord = null;
    this.previewGraphics.clear();
    this.redrawBuilding();
    this.renderEntities();
    this.hud.clearSelection();
    this.hud.update();
  }

  private screenToGrid(screenX: number, screenY: number): { floor: number; col: number } | null {
    const { cols, floors } = this.sim.getState().gridConfig;
    const relX = screenX - this.originX;
    const relY = screenY - this.originY;

    if (relX < 0 || relY < 0) {
      return null;
    }

    const col = Math.floor(relX / this.cellWidth);
    const rowFromTop = Math.floor(relY / this.cellHeight);

    if (col < 0 || col >= cols || rowFromTop < 0 || rowFromTop >= floors) {
      return null;
    }

    const floor = floors - 1 - rowFromTop;
    return { floor, col };
  }

  private gridToScreen(floor: number, col: number): { x: number; y: number } {
    const { floors } = this.sim.getState().gridConfig;
    const rowFromTop = floors - 1 - floor;
    const x = this.originX + col * this.cellWidth;
    const y = this.originY + rowFromTop * this.cellHeight;
    return { x, y };
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.activeRoomTypeId) {
      return;
    }

    const gridPos = this.screenToGrid(pointer.x, pointer.y);
    if (!gridPos) {
      this.previewFloor = null;
      this.previewCol = null;
      this.previewGraphics.clear();
      return;
    }

    if (this.previewFloor !== gridPos.floor || this.previewCol !== gridPos.col) {
      this.previewFloor = gridPos.floor;
      this.previewCol = gridPos.col;
      this.drawPlacementPreview(gridPos.floor, gridPos.col);
    }
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.activeRoomTypeId) {
      return;
    }

    const gridPos = this.screenToGrid(pointer.x, pointer.y);
    if (!gridPos) {
      return;
    }

    const isTouch = pointer.wasTouch;

    if (isTouch) {
      if (
        !this.lastTappedCoord ||
        this.lastTappedCoord.floor !== gridPos.floor ||
        this.lastTappedCoord.col !== gridPos.col
      ) {
        this.lastTappedCoord = { floor: gridPos.floor, col: gridPos.col };
        this.previewFloor = gridPos.floor;
        this.previewCol = gridPos.col;
        this.drawPlacementPreview(gridPos.floor, gridPos.col);
        return;
      }
    }

    this.executeBuild(gridPos.floor, gridPos.col);
  }

  private executeBuild(floor: number, col: number): void {
    if (!this.activeRoomTypeId) {
      return;
    }

    const result = this.sim.dispatch({
      type: 'BUILD_ROOM',
      typeId: this.activeRoomTypeId,
      floor,
      col
    });

    if (result.ok) {
      this.redrawBuilding();
      this.lastTappedCoord = null;
      this.drawPlacementPreview(floor, col);
    } else {
      const reasonKey = result.reason as BuildFailReason;
      const msg = FAIL_MESSAGES[reasonKey] || 'Pembangunan gagal!';
      this.hud.showToast(msg);
    }
  }

  private drawPlacementPreview(floor: number, col: number): void {
    this.previewGraphics.clear();
    if (!this.activeRoomTypeId) {
      return;
    }

    const roomDef = getRoomDefinition(this.activeRoomTypeId);
    if (!roomDef) {
      return;
    }

    const check = this.sim.canBuild(this.activeRoomTypeId, floor, col);
    const { x, y } = this.gridToScreen(floor, col);
    const widthPx = roomDef.width * this.cellWidth;
    const heightPx = this.cellHeight;

    const fillColor = check.ok ? 0x2ecc71 : 0xe74c3c;
    const strokeColor = check.ok ? 0x27ae60 : 0xc0392b;

    this.previewGraphics.fillStyle(fillColor, 0.45);
    this.previewGraphics.fillRect(x + 2, y + 2, widthPx - 4, heightPx - 4);
    this.previewGraphics.lineStyle(2, strokeColor, 0.9);
    this.previewGraphics.strokeRect(x + 2, y + 2, widthPx - 4, heightPx - 4);
  }

  private drawStaticEnvironment(cols: number, floors: number): void {
    const totalWidth = cols * this.cellWidth;
    const totalHeight = floors * this.cellHeight;

    const groundY = this.originY + totalHeight;
    this.backgroundGraphics.fillStyle(0x2d3436, 1);
    this.backgroundGraphics.fillRect(0, groundY, 1280, 720 - groundY);

    this.backgroundGraphics.lineStyle(2, 0x636e72, 0.8);
    this.backgroundGraphics.lineBetween(0, groundY + 2, 1280, groundY + 2);
    this.backgroundGraphics.fillStyle(0xf1c40f, 0.7);
    for (let x = 0; x < 1280; x += 40) {
      this.backgroundGraphics.fillRect(x, groundY + 30, 24, 4);
    }

    this.backgroundGraphics.fillStyle(0x1e272e, 0.95);
    this.backgroundGraphics.fillRect(this.originX, this.originY, totalWidth, totalHeight);

    this.backgroundGraphics.lineStyle(4, 0x485460, 1);
    this.backgroundGraphics.strokeRect(this.originX, this.originY, totalWidth, totalHeight);

    this.backgroundGraphics.lineStyle(1, 0x353b48, 0.7);
    for (let c = 0; c <= cols; c++) {
      const gx = this.originX + c * this.cellWidth;
      this.backgroundGraphics.lineBetween(gx, this.originY, gx, this.originY + totalHeight);
    }
    for (let f = 0; f <= floors; f++) {
      const gy = this.originY + f * this.cellHeight;
      this.backgroundGraphics.lineBetween(this.originX, gy, this.originX + totalWidth, gy);
    }

    for (let f = 0; f < floors; f++) {
      const { y } = this.gridToScreen(f, 0);
      const floorName = f === 0 ? 'Lt Dasar' : `Lt ${f}`;
      this.add.text(this.originX - 12, y + this.cellHeight / 2, floorName, {
        fontSize: '11px',
        color: '#8b949e',
        fontStyle: 'bold'
      }).setOrigin(1, 0.5);
    }

    const entrance = this.gridToScreen(0, 0);
    this.backgroundGraphics.fillStyle(0x34495e, 0.85);
    this.backgroundGraphics.fillRect(entrance.x + 2, entrance.y + 2, this.cellWidth - 4, this.cellHeight - 4);
    this.add.text(entrance.x + this.cellWidth / 2, entrance.y + this.cellHeight / 2, 'PINTU\nMASUK', {
      fontSize: '11px',
      color: '#ecf0f1',
      align: 'center',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    for (let f = 0; f < floors; f++) {
      const stair = this.gridToScreen(f, 6);
      this.backgroundGraphics.fillStyle(0x2f3640, 0.75);
      this.backgroundGraphics.fillRect(stair.x + 2, stair.y + 2, this.cellWidth - 4, this.cellHeight - 4);
      this.backgroundGraphics.lineStyle(1, 0x718093, 0.4);
      this.backgroundGraphics.strokeRect(stair.x + 4, stair.y + 4, this.cellWidth - 8, this.cellHeight - 8);

      this.add.text(stair.x + this.cellWidth / 2, stair.y + this.cellHeight / 2, 'TANGGA\n(RESERVED)', {
        fontSize: '9px',
        color: '#a4b0be',
        align: 'center'
      }).setOrigin(0.5);
    }
  }

  private redrawBuilding(): void {
    this.buildingGraphics.clear();
    this.roomLabels.forEach((label) => label.destroy());
    this.roomLabels = [];

    const state = this.sim.getState();

    for (const room of state.rooms) {
      const visual = getRoomVisualDefinition(room.typeId);
      if (!visual) continue;

      const { x, y } = this.gridToScreen(room.floor, room.startCol);
      const widthPx = room.width * this.cellWidth;
      const heightPx = this.cellHeight;

      const hexColor = parseInt(visual.color.replace('#', ''), 16);

      this.buildingGraphics.fillStyle(hexColor, 0.85);
      this.buildingGraphics.fillRect(x + 2, y + 2, widthPx - 4, heightPx - 4);

      this.buildingGraphics.lineStyle(2, 0xffffff, 0.35);
      this.buildingGraphics.strokeRect(x + 2, y + 2, widthPx - 4, heightPx - 4);

      const label = this.add.text(
        x + widthPx / 2,
        y + 14,
        `${visual.name} #${room.id}`,
        {
          fontSize: '11px',
          color: '#ffffff',
          fontStyle: 'bold',
          align: 'center'
        }
      ).setOrigin(0.5);
      this.roomLabels.push(label);
    }
  }

  private renderEntities(): void {
    this.entitiesGraphics.clear();
    const state = this.sim.getState();

    for (const staff of state.staff) {
      const { x, y } = this.gridToScreen(staff.floor, staff.col);
      const centerX = x + this.cellWidth / 2;
      const centerY = y + this.cellHeight / 2 + 10;

      const staffColor = staff.role === 'receptionist' ? 0x9b59b6 : 0x00cec9;

      this.entitiesGraphics.fillStyle(staffColor, 1);
      this.entitiesGraphics.fillCircle(centerX, centerY - 6, 6);
      this.entitiesGraphics.fillRect(centerX - 5, centerY, 10, 12);

      if (staff.isBusy) {
        this.entitiesGraphics.fillStyle(0xf1c40f, 1);
        this.entitiesGraphics.fillCircle(centerX + 8, centerY - 12, 3);
      }
    }

    for (const patient of state.patients) {
      const { x, y } = this.gridToScreen(patient.floor, patient.col);
      const offsetX = ((patient.id * 17) % 30) - 15;
      const centerX = x + this.cellWidth / 2 + offsetX;
      const centerY = y + this.cellHeight / 2 + 12;

      const pDef = getPatientDefinition(patient.typeId);
      const hexColor = pDef ? parseInt(pDef.color.replace('#', ''), 16) : 0x1abc9c;

      if (patient.state === 'LEAVING_RAGE') {
        this.entitiesGraphics.fillStyle(0xd63031, 1);
      } else {
        this.entitiesGraphics.fillStyle(hexColor, 1);
      }
      this.entitiesGraphics.fillCircle(centerX, centerY - 5, 5);
      this.entitiesGraphics.fillRect(centerX - 4, centerY, 8, 10);

      if (
        patient.state === 'QUEUEING_REG' ||
        patient.state === 'WAITING' ||
        patient.state === 'WALKING_TO_REG' ||
        patient.state === 'WALKING_TO_WAITING'
      ) {
        const ratio = Math.max(0, patient.patienceRemainingMs / patient.totalPatienceMs);
        const barWidth = 24;
        const barX = centerX - barWidth / 2;
        const barY = centerY - 16;

        this.entitiesGraphics.fillStyle(0x000000, 0.6);
        this.entitiesGraphics.fillRect(barX, barY, barWidth, 3);

        const barColor = ratio > 0.5 ? 0x2ecc71 : ratio > 0.25 ? 0xf39c12 : 0xe74c3c;
        this.entitiesGraphics.fillStyle(barColor, 1);
        this.entitiesGraphics.fillRect(barX, barY, Math.round(barWidth * ratio), 3);
      }
    }
  }
}
